import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import {
  FieldDefinition,
  OperatorDefinition,
  QueryBuilderConfig,
  QueryCondition,
  QueryGroup,
  QueryNode,
} from "../../data-model/queryBuilder";

const DATE_INPUT_DISPLAY_FORMAT = "MM/DD/YYYY";

/**
 * Formats a stored timestamp/date rule value for risa-oasis DateInput display.
 *
 * @param stored - ISO instant or MM/DD/YYYY value from Firestore rules
 * @returns Date string in MM/DD/YYYY (UTC calendar date), or the original when not parseable
 */
export function formatTimestampValueForDateInput(stored: string): string {
  if (!stored.trim()) return "";
  const asUtc = moment.utc(stored);
  if (asUtc.isValid()) return asUtc.format(DATE_INPUT_DISPLAY_FORMAT);
  const asDisplay = moment(stored, DATE_INPUT_DISPLAY_FORMAT, true);
  if (asDisplay.isValid()) return asDisplay.format(DATE_INPUT_DISPLAY_FORMAT);
  return stored;
}

/**
 * Converts a DateInput display value to a Firestore-friendly ISO instant at UTC midnight.
 *
 * @param display - Value from DateInput onChange (typically MM/DD/YYYY)
 * @returns ISO string such as `2026-05-26T00:00:00.000Z`
 */
export function formatDateInputValueForStorage(display: string): string {
  if (!display.trim()) return "";
  const parsed = moment.utc(display, DATE_INPUT_DISPLAY_FORMAT, true);
  if (parsed.isValid()) return parsed.startOf("day").toISOString();
  const fallback = moment.utc(display);
  if (fallback.isValid()) return fallback.startOf("day").toISOString();
  return display;
}

export const createEmptyCondition = (): QueryCondition => ({
  type: "condition",
  id: uuidv4(),
  field: "",
  operator: "",
  value: "",
});

export const createEmptyGroup = (): QueryGroup => ({
  type: "group",
  id: uuidv4(),
  conjunction: "AND",
  children: [createEmptyCondition()],
});

export const updateNodeById = (
  root: QueryGroup,
  targetId: string,
  updater: (node: QueryNode) => QueryNode,
): QueryGroup => {
  if (root.id === targetId) {
    return updater(root) as QueryGroup;
  }
  return {
    ...root,
    children: root.children.map((child) => {
      if (child.id === targetId) {
        return updater(child);
      }
      if (child.type === "group") {
        return updateNodeById(child, targetId, updater);
      }
      return child;
    }),
  };
};

export const removeNodeById = (
  root: QueryGroup,
  targetId: string,
): QueryGroup => {
  return {
    ...root,
    children: root.children
      .filter((child) => child.id !== targetId)
      .map((child) => {
        if (child.type === "group") {
          return removeNodeById(child, targetId);
        }
        return child;
      }),
  };
};

export const addChildToGroup = (
  root: QueryGroup,
  groupId: string,
  newChild: QueryNode,
): QueryGroup => {
  if (root.id === groupId) {
    return { ...root, children: [...root.children, newChild] };
  }
  return {
    ...root,
    children: root.children.map((child) => {
      if (child.type === "group") {
        return addChildToGroup(child, groupId, newChild);
      }
      return child;
    }),
  };
};

export const getFieldDef = (
  config: QueryBuilderConfig,
  fieldValue: string,
): FieldDefinition | undefined => {
  return config.fields.find((f) => f.value === fieldValue);
};

/** Legacy symbolic operators stored on older pharmacy order update rules. */
const OPERATOR_VALUE_ALIASES: Record<string, string> = {
  "=": "equals",
  "!=": "not_equals",
  "≠": "not_equals",
};

/**
 * Resolves an operator definition, including legacy symbolic values (`=`, `!=`).
 *
 * @param fieldDef - Field definition from the query builder config
 * @param operatorValue - Operator value stored on the condition
 * @returns Matching operator definition, if any
 */
export const getOperatorDef = (
  fieldDef: FieldDefinition,
  operatorValue: string,
): OperatorDefinition | undefined => {
  const direct = fieldDef.operators.find((op) => op.value === operatorValue);
  if (direct) return direct;

  const aliasTarget = OPERATOR_VALUE_ALIASES[operatorValue];
  if (aliasTarget) {
    return fieldDef.operators.find((op) => op.value === aliasTarget);
  }

  return undefined;
};

/**
 * Maps a stored operator value to the canonical option value used in operator dropdowns.
 *
 * @param fieldDef - Field definition from the query builder config
 * @param operatorValue - Operator value stored on the condition
 * @returns Canonical operator value for Select components
 */
export const resolveOperatorValueForUi = (
  fieldDef: FieldDefinition,
  operatorValue: string,
): string => getOperatorDef(fieldDef, operatorValue)?.value ?? operatorValue;
